import { episodeBeatSection, episodeSubtitleSections, reduceEpisodeSubtitleIntent,
    resolveEpisodeBeatSelection, withSceneSubtitle } from '@/_pure/episode';
import { coreEpisodes, resolveCoreEpisode } from '@/adapters/core/episodeCatalog';
import type { SpaceAdapterRuntimeController } from '@/_contract/types';
import type { Config, CosmosDataModel, RuntimeState } from '@/adapters/core/_contract/config';

const SECTION_EPISODE = 'core-episode-selector';
const ACTION_PLAY_PAUSE = 'episode-play-pause';
const ACTION_NEXT = 'episode-next';
const ACTION_PREV = 'episode-prev';
const ACTION_RESET = 'episode-reset';

function enterEpisode(state: RuntimeState, handle: string, soundDefault = false): RuntimeState {
    return {
        ...state,
        sceneIndex: 0,
        accumulatedReadMs: 0,
        episode: {
            episodeHandle: handle,
            beatIndex: 0,
            status: 'reading',
            cueIndex: 0,
            playbackAdvanceMode: 'manual',
            isPlaying: false,
            soundEnabled: state.episode?.soundEnabled ?? soundDefault,
            subtitlesEnabled: state.episode?.subtitlesEnabled ?? true,
            ...(state.episode?.subtitleTrace ? { subtitleTrace: state.episode.subtitleTrace } : {}),
            revision: (state.episode?.revision ?? 0) + 1,
        },
    };
}

function moveBeat(state: RuntimeState, beatIndex: number): RuntimeState {
    const { episode, sceneIndex = 0 } = state;
    if (!episode) return state;
    const { episode: ep, scene } = resolveCoreEpisode(episode.episodeHandle, sceneIndex);
    if (beatIndex >= scene.beats.length && sceneIndex < ep.scenes.length - 1) {
        return { ...state, sceneIndex: sceneIndex + 1, accumulatedReadMs: 0, episode: {
            ...episode, beatIndex: 0, status: 'reading', cueIndex: 0,
            narrationCompleted: false, subtitleIndex: undefined, revision: episode.revision + 1, error: undefined,
        } };
    }
    if (beatIndex < 0 && sceneIndex > 0) {
        const prevBeats = ep.scenes[sceneIndex - 1]!.beats;
        return { ...state, sceneIndex: sceneIndex - 1, accumulatedReadMs: 0, episode: {
            ...episode, beatIndex: prevBeats.length - 1, status: 'reading', cueIndex: 0,
            narrationCompleted: false, subtitleIndex: undefined, revision: episode.revision + 1, error: undefined,
        } };
    }
    if (beatIndex < 0 || beatIndex >= scene.beats.length) {
        return { ...state, accumulatedReadMs: 0, episode: { ...episode, isPlaying: false, narrationRequested: false } };
    }
    return { ...state, accumulatedReadMs: 0, episode: {
        ...episode, beatIndex, status: 'reading', cueIndex: 0,
        narrationCompleted: false, subtitleIndex: undefined, revision: episode.revision + 1, error: undefined,
    } };
}

export function createInitialRuntimeState(): RuntimeState {
    return {};
}

export const runtimeController = {
    createInitialRuntimeState: () => createInitialRuntimeState(),

    buildOverlayModel: (args) => {
        const { runtimeState: state } = args;
        const { episode, sceneIndex = 0 } = state;
        const resolved = episode ? resolveCoreEpisode(episode.episodeHandle, sceneIndex) : undefined;
        const ep = resolved?.episode;
        const scene = resolved?.scene;
        const beat = episode && scene ? scene.beats[episode.beatIndex] : undefined;
        const canNext = Boolean(episode && ep && scene && (
            episode.beatIndex < scene.beats.length - 1 || sceneIndex < ep.scenes.length - 1
        ));
        const canPrev = Boolean(episode && (episode.beatIndex > 0 || sceneIndex > 0));
        return {
            overlayId: 'core-space',
            title: ep?.title ?? 'Core Space',
            ...(episode && ep && scene && beat ? { timeline: {
                label: 'Episode timeline', positionSectionId: 'episode-beat', controlsSectionId: 'core-episode-playback',
            } } : {}),
            sections: [
                {
                    sectionKind: 'select', sectionId: SECTION_EPISODE, label: 'Episode',
                    selectedValue: episode?.episodeHandle ?? '',
                    options: [
                        { value: '', label: 'Explore freely' },
                        ...coreEpisodes.map((ep) => ({ value: ep.handle, label: ep.title })),
                    ],
                },
                ...(episode && ep && scene && beat ? [
                    episodeBeatSection(ep.scenes, sceneIndex, episode.beatIndex),
                    ...episodeSubtitleSections(episode),
                    {
                        sectionKind: 'actions' as const, sectionId: 'core-episode-playback',
                        label: `${scene.title} (${sceneIndex + 1}/${ep.scenes.length}; ${episode.beatIndex + 1}/${scene.beats.length})`,
                        description: episode.error ?? 'Ready',
                        actions: [
                            { actionId: ACTION_PLAY_PAUSE, playbackRole: 'toggle' as const,
                                label: episode.isPlaying ? 'Pause' : 'Play',
                                isActive: episode.isPlaying, enabled: canNext || episode.isPlaying },
                            { actionId: ACTION_RESET, playbackRole: 'reset' as const, label: 'Reset', enabled: true },
                            { actionId: ACTION_PREV, playbackRole: 'step' as const, label: 'Prev', enabled: canPrev, allowDuringPlayback: true },
                            { actionId: ACTION_NEXT, playbackRole: 'step' as const, label: 'Next', enabled: canNext, allowDuringPlayback: true },
                        ],
                    },
                    ...(episode.error ? [{ sectionKind: 'actions' as const, sectionId: 'episode-error',
                        label: 'Episode error', description: episode.error, actions: [] }] : []),
                ] : []),
            ],
            ...withSceneSubtitle(episode, beat),
        };
    },

    reduceOverlayIntent: (args) => {
        const { runtimeState: state, intent } = args;
        if (intent.overlayIntentKind === 'select-changed' && intent.sectionId === 'episode-beat' && state.episode) {
            const { episode } = resolveCoreEpisode(state.episode.episodeHandle, state.sceneIndex ?? 0);
            const target = resolveEpisodeBeatSelection(episode.scenes, intent.value);
            return moveBeat({ ...state, sceneIndex: target.sceneIndex,
                episode: { ...state.episode, isPlaying: false } }, target.beatIndex);
        }
        if (intent.overlayIntentKind === 'select-changed' && intent.sectionId === SECTION_EPISODE) {
            return intent.value === ''
                ? { ...state, episode: undefined, sceneIndex: undefined, accumulatedReadMs: undefined }
                : enterEpisode(state, intent.value, args.config.episodeSoundDefault);
        }
        if (intent.overlayIntentKind === 'action-triggered') {
            if (intent.actionId === ACTION_PLAY_PAUSE && state.episode) {
                return { ...state, accumulatedReadMs: 0, episode: { ...state.episode, isPlaying: !state.episode.isPlaying } };
            }
            if (intent.actionId === 'episode-continue' && state.episode?.isPlaying) {
                return moveBeat(state, state.episode.beatIndex + 1);
            }
            if (intent.actionId === ACTION_NEXT && state.episode) {
                return moveBeat({ ...state, episode: { ...state.episode, isPlaying: false } }, state.episode.beatIndex + 1);
            }
            if (intent.actionId === ACTION_PREV && state.episode) {
                return moveBeat({ ...state, episode: { ...state.episode, isPlaying: false } }, state.episode.beatIndex - 1);
            }
            if (intent.actionId === ACTION_RESET && state.episode) return enterEpisode(state, state.episode.episodeHandle);
        }
        if (state.episode) {
            if (intent.overlayIntentKind === 'checkbox-changed' && intent.sectionId === 'episode-sound') {
                return { ...state, episode: { ...state.episode, soundEnabled: intent.checked,
                    ...(state.episode.error ? { error: undefined, narrationCompleted: false,
                        narrationRequested: true, revision: state.episode.revision + 1 } : {}) } };
            }
            const reduced = reduceEpisodeSubtitleIntent(state.episode, intent);
            if (reduced) return { ...state, episode: reduced };
        }
        return state;
    },

    reducePlaybackSuspension: ({ runtimeState, suspended, resumeState }) => ({
        ...runtimeState,
        ...(runtimeState.episode ? { episode: { ...runtimeState.episode,
            isPlaying: !suspended && resumeState.episode?.isPlaying === true,
        } } : {}),
    }),

} satisfies SpaceAdapterRuntimeController<Config, RuntimeState, CosmosDataModel>;
